import { loadStripe } from "@stripe/stripe-js";

/**
 * Coming back from a Stripe redirect taken during the basket checkout's card
 * step: 3-D Secure challenges that leave the page, and bank-redirect /
 * pay-later methods (iDEAL, Bancontact, Klarna...). `confirmPayment` only
 * leaves the page when the method needs it (`redirect: "if_required"`); when
 * it does, Stripe sends the buyer to the `return_url` with `payment_intent`,
 * `payment_intent_client_secret` and `redirect_status` added.
 *
 * The return address is the host page the buyer was on (its own query kept,
 * never another host), plus a marker naming the school and the basket order.
 * The bundle reads it as it loads, on whichever page that is (a Website v2
 * page or a school's own site, with or without a `<baluu-basket>`), takes the
 * keys off the address, and opens the basket checkout to finish there:
 * recorded and confirmed when paid, the basket back with a notice when not.
 *
 * v1's equivalent is the widget's full-page checkout
 * (StripePaymentInner's return_url, useBasketCheckout's return effect).
 * Unlike v1, `redirect_status` is not written into the return URL: Stripe
 * adds its own, and a hardcoded "succeeded" beside Stripe's "failed" is
 * exactly the ambiguity v1 has to defend against.
 */

/** Names the school whose basket checkout sent the buyer to Stripe. */
export const BASKET_RETURN_PARAM = "baluu_basket_return";
/** The basket order being paid. Same key the hosted-page return uses. */
export const BASKET_ORDER_PARAM = "basket_order_id";

/** What Stripe appends to a return_url. */
const STRIPE_RETURN_PARAMS = [
  "payment_intent",
  "payment_intent_client_secret",
  "redirect_status",
] as const;

const RETURN_PARAMS = [BASKET_RETURN_PARAM, BASKET_ORDER_PARAM, ...STRIPE_RETURN_PARAMS];

/**
 * How the payment stands on return. "paid" covers an authorised hold
 * (requires_capture) as well as a captured charge; "processing" is a method
 * that settles later (bank debits) — the order is placed and confirmed by
 * webhook when it clears.
 */
export type PaymentReturnOutcome = "paid" | "processing" | "failed";

export type BasketPaymentReturn = {
  businessId: string;
  basketOrderId: string;
  paymentIntentId: string;
  /** Lets the page ask Stripe for the PaymentIntent's real status. */
  clientSecret: string | null;
  /** What `redirect_status` said. Checked against Stripe where possible. */
  outcome: PaymentReturnOutcome;
};

/**
 * The `return_url` for a basket order's card payment: this page's address
 * with any earlier return's keys and the fragment removed (Stripe appends its
 * own query, and a stale `payment_intent` beside it would be ambiguous), plus
 * the marker. Null on a page that isn't http(s), where Stripe can't return.
 */
export function basketPaymentReturnUrl(
  href: string,
  { businessId, basketOrderId }: { businessId: string; basketOrderId: string },
): string | null {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;
  for (const key of RETURN_PARAMS) url.searchParams.delete(key);
  url.hash = "";
  url.searchParams.set(BASKET_RETURN_PARAM, businessId);
  url.searchParams.set(BASKET_ORDER_PARAM, basketOrderId);
  return url.toString();
}

/** A Stripe `redirect_status` (or PaymentIntent status) as an outcome. */
export function paymentOutcomeFor(status: string | null | undefined): PaymentReturnOutcome {
  if (status === "succeeded" || status === "requires_capture") return "paid";
  if (status === "processing") return "processing";
  return "failed";
}

/**
 * The basket payment this address is a return from, or null. Needs the
 * marker, the order, the PaymentIntent and exactly one `redirect_status`: a
 * repeated status can't be read either way, and is treated as no return.
 */
export function readBasketPaymentReturn(href: string): BasketPaymentReturn | null {
  let params: URLSearchParams;
  try {
    params = new URL(href).searchParams;
  } catch {
    return null;
  }
  const businessId = params.get(BASKET_RETURN_PARAM);
  const basketOrderId = params.get(BASKET_ORDER_PARAM);
  const paymentIntentId = params.get("payment_intent");
  const statuses = params.getAll("redirect_status");
  if (!businessId || !basketOrderId || !paymentIntentId || statuses.length !== 1) return null;
  return {
    businessId,
    basketOrderId,
    paymentIntentId,
    clientSecret: params.get("payment_intent_client_secret"),
    outcome: paymentOutcomeFor(statuses[0]),
  };
}

/** The address with the return's keys taken off, everything else kept. */
export function withoutBasketPaymentReturn(href: string): string {
  const url = new URL(href);
  for (const key of RETURN_PARAMS) url.searchParams.delete(key);
  return url.toString();
}

/**
 * What the overlay needs to finish the order after the round trip, kept in
 * this tab while the buyer is at Stripe (the page reloads on return and the
 * overlay's state is gone).
 */
export type BasketPaymentReturnContext = {
  businessId: string;
  apiUrl?: string;
  primaryColor?: string;
  gtagConversionId?: string;
  gtagConversionLabel?: string;
  /** For asking Stripe the PaymentIntent's status on return. */
  stripePublicKey?: string;
  /** The connected account, for a direct charge. */
  stripeAccount?: string;
  /** What the conversion and the confirmation quote. */
  grossTotal?: number;
  netTotal?: number;
  total?: number;
  discountAmount?: number;
  currency?: string;
  email?: string;
  itemCount?: number;
  /** A deposit paid with a request: the confirmation says "request sent". */
  depositRequest?: { depositPence: number; remainingPence: number } | null;
};

const contextKey = (basketOrderId: string) => `baluu_basket_return_${basketOrderId}`;

/** Called when the card step opens for an order. */
export function rememberBasketPaymentReturn(
  basketOrderId: string,
  context: BasketPaymentReturnContext,
  storage: Pick<Storage, "setItem"> | null = safeSessionStorage(),
): void {
  try {
    storage?.setItem(contextKey(basketOrderId), JSON.stringify(context));
  } catch {
    // No storage (a private window): the return still finishes the order,
    // without the figures for the conversion.
  }
}

/** The context kept for an order, removed as it is read. */
export function takeBasketPaymentReturn(
  basketOrderId: string,
  storage: Pick<Storage, "getItem" | "removeItem"> | null = safeSessionStorage(),
): BasketPaymentReturnContext | null {
  try {
    const raw = storage?.getItem(contextKey(basketOrderId));
    storage?.removeItem(contextKey(basketOrderId));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? (parsed as BasketPaymentReturnContext) : null;
  } catch {
    return null;
  }
}

/** Forgets an order's context once it finished in the page. */
export function forgetBasketPaymentReturn(
  basketOrderId: string,
  storage: Pick<Storage, "removeItem"> | null = safeSessionStorage(),
): void {
  try {
    storage?.removeItem(contextKey(basketOrderId));
  } catch {
    // Nothing to forget it from.
  }
}

// The PaymentIntents this browser has already finished a return for, so the
// back button or a bookmarked return address never confirms (and counts) one
// twice.
const HANDLED_KEY = "baluu-basket-payment-returns";
const HANDLED_KEPT = 20;

export function returnAlreadyHandled(
  paymentIntentId: string,
  storage: Pick<Storage, "getItem"> | null = safeLocalStorage(),
): boolean {
  try {
    const seen: unknown = JSON.parse(storage?.getItem(HANDLED_KEY) || "[]");
    return Array.isArray(seen) && seen.includes(paymentIntentId);
  } catch {
    return false;
  }
}

export function markReturnHandled(
  paymentIntentId: string,
  storage: Pick<Storage, "getItem" | "setItem"> | null = safeLocalStorage(),
): void {
  try {
    const seen: unknown = JSON.parse(storage?.getItem(HANDLED_KEY) || "[]");
    const list = Array.isArray(seen)
      ? seen.filter((id) => typeof id === "string" && id !== paymentIntentId)
      : [];
    storage?.setItem(HANDLED_KEY, JSON.stringify([...list, paymentIntentId].slice(-HANDLED_KEPT)));
  } catch {
    // No storage: nothing to remember it by.
  }
}

function safeSessionStorage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

function safeLocalStorage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

/** The return as the overlay receives it (its `payment-return` attribute). */
export type BasketPaymentReturnProp = BasketPaymentReturn & {
  context: BasketPaymentReturnContext | null;
};

/**
 * Back from a Stripe redirect taken in the basket checkout: the bundle runs
 * this as it loads, on any page. The return's keys come off the address
 * first (a reload or a shared link never replays it), and the basket checkout
 * opens to finish the order — or, for a PaymentIntent this browser already
 * finished, nothing happens. `open` puts the overlay on the page; returns
 * what it was given, or null.
 */
export function resumeBasketPaymentReturn(
  open: (ret: BasketPaymentReturnProp) => void,
): BasketPaymentReturnProp | null {
  if (typeof window === "undefined") return null;
  const href = window.location.href;
  const ret = readBasketPaymentReturn(href);
  if (!ret) return null;
  try {
    window.history.replaceState(window.history.state, "", withoutBasketPaymentReturn(href));
  } catch {
    // An address the page can't rewrite: leave it (the handled list below
    // still stops a replay).
  }
  if (returnAlreadyHandled(ret.paymentIntentId)) return null;
  const context = takeBasketPaymentReturn(ret.basketOrderId);
  // A context kept for another school's basket is not this one's.
  const prop: BasketPaymentReturnProp = {
    ...ret,
    context: context && context.businessId === ret.businessId ? context : null,
  };
  open(prop);
  return prop;
}

type RetrievePaymentIntent = (
  publicKey: string,
  stripeAccount: string | undefined,
  clientSecret: string,
) => Promise<{ id: string; status: string } | null>;

const retrieveWithStripeJs: RetrievePaymentIntent = async (publicKey, stripeAccount, clientSecret) => {
  const stripe = await loadStripe(publicKey, stripeAccount ? { stripeAccount } : undefined);
  const result = await stripe?.retrievePaymentIntent(clientSecret);
  return result?.paymentIntent ? { id: result.paymentIntent.id, status: result.paymentIntent.status } : null;
};

/**
 * How the payment really stands. `redirect_status` is only what the address
 * says, and an address can be typed; with the publishable key kept for the
 * trip and the client secret Stripe returned, Stripe is asked instead
 * (`verified`). Without them — no storage, or Stripe unreachable — the
 * address is all there is: the order is still finished (the API and the
 * webhook decide what is really paid), but nothing is reported as a purchase.
 */
export async function verifyPaymentReturn(
  ret: BasketPaymentReturnProp,
  retrieve: RetrievePaymentIntent = retrieveWithStripeJs,
): Promise<{ outcome: PaymentReturnOutcome; verified: boolean }> {
  const publicKey = ret.context?.stripePublicKey;
  if (publicKey && ret.clientSecret) {
    try {
      const intent = await retrieve(publicKey, ret.context?.stripeAccount, ret.clientSecret);
      if (intent) {
        // A secret for some other PaymentIntent proves nothing about this one.
        if (intent.id !== ret.paymentIntentId) return { outcome: ret.outcome, verified: false };
        return { outcome: paymentOutcomeFor(intent.status), verified: true };
      }
    } catch {
      // Stripe unreachable: fall back on the address.
    }
  }
  return { outcome: ret.outcome, verified: false };
}
